import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import "./StickyHorizontalScrollbar.scss";

/**
 * A single, floating horizontal scrollbar for a wide {@link RecordTable} that
 * makes horizontal scrolling reachable while looking at the *top* of a long
 * list, without scrolling all the way down first.
 *
 * ## One scrollbar, not two — it "merges" with the native one
 *
 * The table's real horizontal scroll container (mui-datatables' `responsiveBase`
 * div) keeps its own **native** horizontal scrollbar at the bottom of the table
 * content (above the pagination footer). That native scrollbar is the canonical
 * one and is styled by the app-wide `body ::-webkit-scrollbar` rule like every
 * other scrollbar in the Console.
 *
 * This component adds a *proxy* (mirror) bar that is shown **only while the
 * native scrollbar is scrolled out of view** (below the fold). It floats pinned
 * to the bottom of the viewport (`position: sticky; bottom: 0`) and its scroll
 * position is kept in sync, both ways, with `responsiveBase`. The instant the
 * native scrollbar scrolls back into view (the user reaches the bottom of the
 * list), the proxy hides and the native bar takes over at its resting position
 * above the pagination — so the user always sees exactly **one** horizontal
 * scrollbar, which appears to "stay put" / merge as they scroll. It is a no-op
 * for tables that fit horizontally.
 *
 * The proxy carries no custom scrollbar styling, so it inherits the same global
 * `::-webkit-scrollbar` appearance as the native bar — the two are visually
 * identical.
 */
interface StickyHorizontalScrollbarProps {
    /**
     * Ref to the `.recordTable` wrapper element. The real horizontal scroll
     * container (mui-datatables `responsiveBase`) is resolved beneath it.
     */
    tableWrapperRef: RefObject<HTMLElement>;
}

// mui-datatables names its (default `responsive: "vertical"`) scroll wrapper with
// a JSS-hashed class that contains this substring — matching the app-wide SCSS
// selector `div[class*="responsiveBase"]`.
const SCROLL_NODE_SELECTOR = '[class*="responsiveBase"]';

// Ignore sub-pixel deltas so rounding doesn't cause a 1px sync ping-pong.
const EPS = 1;

export default function StickyHorizontalScrollbar({
    tableWrapperRef,
}: StickyHorizontalScrollbarProps) {
    const proxyRef = useRef<HTMLDivElement>(null);
    const [scrollWidth, setScrollWidth] = useState(0);
    // The proxy is shown only when the table overflows horizontally AND the
    // native scrollbar (at the bottom of `responsiveBase`) is currently below the
    // viewport fold — i.e. not reachable without scrolling down.
    const [visible, setVisible] = useState(false);

    const getScrollNode = useCallback(
        (): HTMLElement | null =>
            tableWrapperRef.current?.querySelector<HTMLElement>(SCROLL_NODE_SELECTOR) ?? null,
        [tableWrapperRef]
    );

    useEffect(() => {
        const wrapper = tableWrapperRef.current;
        if (!wrapper) {
            return;
        }
        const scrollNode = getScrollNode();

        const syncFromContainer = () => {
            const node = getScrollNode();
            const proxy = proxyRef.current;
            if (!node || !proxy) {
                return;
            }
            if (Math.abs(proxy.scrollLeft - node.scrollLeft) > EPS) {
                proxy.scrollLeft = node.scrollLeft;
            }
        };

        // Recompute overflow + whether the native scrollbar is reachable, and keep
        // the proxy's thumb aligned with the container. Called on any scroll (of
        // the page or an inner scroll container), resize, or content resize.
        const update = () => {
            const node = getScrollNode();
            if (!node) {
                setVisible(false);
                return;
            }
            setScrollWidth(node.scrollWidth);
            const overflowing = node.scrollWidth - node.clientWidth > EPS;
            // The native horizontal scrollbar sits at the bottom edge of the
            // scroll container. It is reachable when that bottom edge is within
            // the viewport; otherwise it is below the fold and we surface the
            // floating proxy instead. `+ EPS` avoids a flicker exactly at the edge.
            const rect = node.getBoundingClientRect();
            const nativeBarReachable = rect.bottom <= window.innerHeight + EPS;
            setVisible(overflowing && !nativeBarReachable);
            syncFromContainer();
        };

        update();

        const ro = new ResizeObserver(update);
        ro.observe(wrapper);
        if (scrollNode) {
            ro.observe(scrollNode);
            const tableEl = scrollNode.querySelector("table");
            if (tableEl) {
                ro.observe(tableEl);
            }
            // Mirror the real container's horizontal scroll onto the proxy.
            scrollNode.addEventListener("scroll", syncFromContainer, { passive: true });
        }
        // Capture-phase listener catches scroll events from ANY ancestor/inner
        // scroll container (scroll does not bubble), so `nativeBarReachable`
        // tracks both full-page scrolling and the CO-list inner scroll container.
        //
        // Skip the proxy's OWN scroll events. When the user drags the proxy,
        // `onProxyScroll` mirrors proxy -> container. This capture listener would
        // also fire for that same scroll event (capture reaches the proxy), and
        // `update()` calls `syncFromContainer()` which writes proxy.scrollLeft =
        // container.scrollLeft. Because capture runs BEFORE the proxy's own
        // target-phase `onProxyScroll`, it would reset the proxy back to the
        // container's stale position, pinning horizontal scroll at 0 and making
        // the floating bar impossible to drag. Ignoring proxy-origin events keeps
        // the proxy -> container direction owned solely by `onProxyScroll`.
        const onDocumentScroll = (ev: Event) => {
            if (ev.target === proxyRef.current) {
                return;
            }
            update();
        };
        document.addEventListener("scroll", onDocumentScroll, { capture: true, passive: true });
        window.addEventListener("resize", update, { passive: true });

        return () => {
            ro.disconnect();
            if (scrollNode) {
                scrollNode.removeEventListener("scroll", syncFromContainer);
            }
            document.removeEventListener("scroll", onDocumentScroll, {
                capture: true,
            } as EventListenerOptions);
            window.removeEventListener("resize", update);
        };
    }, [getScrollNode, tableWrapperRef]);

    // Mirror proxy -> container (user drags the sticky scrollbar).
    const onProxyScroll = useCallback(() => {
        const node = getScrollNode();
        const proxy = proxyRef.current;
        if (!node || !proxy) {
            return;
        }
        if (Math.abs(node.scrollLeft - proxy.scrollLeft) > EPS) {
            node.scrollLeft = proxy.scrollLeft;
        }
    }, [getScrollNode]);

    return (
        <div
            ref={proxyRef}
            className={`stickyHScrollbar${visible ? "" : " stickyHScrollbar--hidden"}`}
            onScroll={onProxyScroll}
            aria-hidden="true"
        >
            <div className="stickyHScrollbar__spacer" style={{ width: scrollWidth }} />
        </div>
    );
}
